import { Canvas } from '@react-three/fiber';
import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { PCFSoftShadowMap } from 'three';
import { useSnapshot } from 'valtio';

import Grid from '@cartwhl/3d/r3f/components/Grid';
import GroundPlane from '@cartwhl/3d/r3f/components/GroundPlane';
import { isNonProduction } from '@cartwhl/common/utils';

import useCanvasResizer from 'hooks/useCanvasResizer';
import { animationPlayerStore, sceneSettingsStore } from 'stores';

import usePlayerHotkeys from '../usePlayerHotkeys';
import CanvasError from './CanvasError';
import DefaultAnimations from './DefaultAnimations';
import SceneSetup from './SceneSetup';
import StageFromBVHNew from './StageFromBVHNew';

export default function AnimationMainPlayerNew({
  children,
  showDefaultAnimations,
}: {
  children?: ReactNode;
  showDefaultAnimations?: boolean;
}) {
  const { mixerReady, disabled } = useSnapshot(animationPlayerStore.state);
  const {
    cameraSettings: { aspectRatio },
    backgroundSettings: { showGrid, showGroundPlane, gridCellSize = 1 },
  } = useSnapshot(sceneSettingsStore.state);
  const [canvasStyle, setCanvasStyle] = useState<CSSProperties>({
    borderRadius: 'inherit',
  });

  const isNonProductionEnvironment = isNonProduction();

  usePlayerHotkeys(
    'main',
    animationPlayerStore.defaultInstance,
    sceneSettingsStore,
  );

  useEffect(() => {
    const styles: CSSProperties = { ...canvasStyle };

    // For use when we want to override the canvas size.
    // particularly useful for rendering mp4 videos at a specific resolution
    const params = new URLSearchParams(window.location.search);
    if (params.has('canvasWidth') && params.has('canvasHeight')) {
      styles.width = parseInt(params.get('canvasWidth') ?? '1280');
      styles.height = parseInt(params.get('canvasHeight') ?? '960');
    }
    setCanvasStyle(styles);
  }, []);

  useCanvasResizer(aspectRatio);

  return (
    <>
      <div
        id="threejs-div"
        className={`transitiom-colors relative flex h-full w-full flex-none items-center justify-center overflow-hidden rounded-3xl ${
          aspectRatio === -1 ? '' : 'bg-gray-75 dark:bg-gray-60'
        }`}
      >
        {disabled && <div className="bg-gray-90/10 absolute inset-0 z-1" />}
        <ErrorBoundary fallback={<CanvasError className="pb-16" />}>
          <Canvas
            id="r3f-canvas-holder"
            gl={{ preserveDrawingBuffer: true }}
            style={canvasStyle}
            shadows={{
              enabled: true,
              autoUpdate: true,
              type: PCFSoftShadowMap,
            }}
          >
            <SceneSetup
              animationPlayerStore={animationPlayerStore.defaultInstance}
            />
            {children}

            {showDefaultAnimations ? (
              <DefaultAnimations />
            ) : (
              <StageFromBVHNew />
            )}
            {showGrid && (
              <Grid cellSize={gridCellSize} sectionSize={gridCellSize * 5} />
            )}
            {isNonProductionEnvironment && showGroundPlane && <GroundPlane />}
          </Canvas>
        </ErrorBoundary>
      </div>
      {mixerReady && (
        <div className="hidden" id="download-video-can-generate" />
      )}
    </>
  );
}
